/**
 * Platform-plane Auth v2 helpers (private dashboard code).
 * First-party `/v1/platform/auth/*` only — no Hosted portal transaction login,
 * no published `@knotree/client` dependency for platform owners.
 */

import {
  ApiClientError,
  api,
  isUsablePlatformSession,
  type Session,
} from "./api";

export type PlatformAuthView =
  | "sign-in"
  | "sign-up"
  | "otp"
  | "forgot"
  | "reset";

export type PlatformOtpPending = {
  email: string;
  userId?: string;
  from: "signup" | "signin" | "forgot";
};

export type PlatformAuthSuccess = {
  kind: "session";
  session: Session & { access_token: string; refresh_token: string };
};

export type PlatformAuthStep =
  | PlatformAuthSuccess
  | { kind: "otp"; pending: PlatformOtpPending }
  | { kind: "reset"; pending: PlatformOtpPending; message?: string }
  | { kind: "error"; message: string; code?: string }
  | { kind: "message"; message: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isLikelyEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

export function digitsOnlyOtp(value: string): string {
  return value.replace(/\D/g, "").slice(0, 6);
}

function errMessage(error: unknown, fallback = "Something went wrong. Please try again."): string {
  if (error instanceof ApiClientError) return error.message || fallback;
  if (error instanceof Error) return error.message || fallback;
  return fallback;
}

function emailNotVerifiedDetails(
  error: unknown,
): { user_id?: string; email?: string } | undefined {
  if (!(error instanceof ApiClientError) || error.code !== "EMAIL_NOT_VERIFIED") {
    return undefined;
  }
  const details = error.details;
  if (details && typeof details === "object") {
    return details as { user_id?: string; email?: string };
  }
  return undefined;
}

/** Sign in against first-party platform auth. */
export async function platformSignIn(
  email: string,
  password: string,
): Promise<PlatformAuthStep> {
  const trimmed = email.trim();
  if (!trimmed || !password) {
    return { kind: "error", message: "Email and password are required." };
  }
  if (!isLikelyEmail(trimmed)) {
    return { kind: "error", message: "Please enter a valid email address." };
  }
  try {
    const { data } = await api.signin(trimmed, password);
    if (isUsablePlatformSession(data)) {
      return { kind: "session", session: data };
    }
    return {
      kind: "error",
      message: "Sign-in did not return a usable session.",
    };
  } catch (error) {
    const details = emailNotVerifiedDetails(error);
    if (details || (error instanceof ApiClientError && error.code === "EMAIL_NOT_VERIFIED")) {
      const pendingEmail = details?.email || trimmed;
      return {
        kind: "otp",
        pending: {
          email: pendingEmail,
          userId: details?.user_id,
          from: "signin",
        },
      };
    }
    return {
      kind: "error",
      message: errMessage(error),
      code: error instanceof ApiClientError ? error.code : undefined,
    };
  }
}

/** Sign up against first-party platform auth. */
export async function platformSignUp(
  email: string,
  password: string,
  username?: string,
): Promise<PlatformAuthStep> {
  const trimmed = email.trim();
  if (!trimmed || !password) {
    return { kind: "error", message: "Email and password are required." };
  }
  if (!isLikelyEmail(trimmed)) {
    return { kind: "error", message: "Please enter a valid email address." };
  }
  if (password.length < 8) {
    return { kind: "error", message: "Password must be at least 8 characters." };
  }
  try {
    const { data } = await api.signup(
      trimmed,
      password,
      username?.trim() || undefined,
    );
    if (isUsablePlatformSession(data)) {
      return { kind: "session", session: data };
    }
    if (data?.status === "pending_verification" || data?.user) {
      return {
        kind: "otp",
        pending: {
          email: data.user?.email || trimmed,
          userId: data.user?.id,
          from: "signup",
        },
      };
    }
    return {
      kind: "error",
      message: "Sign-up did not return a verification challenge.",
    };
  } catch (error) {
    return {
      kind: "error",
      message: errMessage(error),
      code: error instanceof ApiClientError ? error.code : undefined,
    };
  }
}

/** Verify 6-digit email OTP and obtain a platform session. */
export async function platformVerifyEmailOtp(input: {
  code: string;
  email: string;
  userId?: string;
}): Promise<PlatformAuthStep> {
  const code = digitsOnlyOtp(input.code);
  if (code.length !== 6) {
    return { kind: "error", message: "Enter the 6-digit code from your email." };
  }
  try {
    const { data } = await api.verifyEmailOtp({
      code,
      email: input.email,
      userId: input.userId,
    });
    if (isUsablePlatformSession(data)) {
      return { kind: "session", session: data };
    }
    return {
      kind: "error",
      message: "Verification did not return a usable session.",
    };
  } catch (error) {
    return {
      kind: "error",
      message: errMessage(error),
      code: error instanceof ApiClientError ? error.code : undefined,
    };
  }
}

/** Resend verification OTP. */
export async function platformResendEmailOtp(input: {
  email: string;
  userId?: string;
}): Promise<PlatformAuthStep> {
  try {
    await api.resendEmailOtp({
      email: input.email,
      userId: input.userId,
    });
    return { kind: "message", message: "A new code is on its way." };
  } catch (error) {
    return {
      kind: "error",
      message: errMessage(error),
      code: error instanceof ApiClientError ? error.code : undefined,
    };
  }
}

/** Start forgot-password OTP flow. */
export async function platformForgotPassword(
  email: string,
): Promise<PlatformAuthStep> {
  const trimmed = email.trim();
  if (!trimmed) {
    return { kind: "error", message: "Email is required." };
  }
  if (!isLikelyEmail(trimmed)) {
    return { kind: "error", message: "Please enter a valid email address." };
  }
  try {
    await api.forgotPassword(trimmed);
    return {
      kind: "reset",
      pending: { email: trimmed, from: "forgot" },
      message: "If an account exists, a reset code was sent.",
    };
  } catch (error) {
    return {
      kind: "error",
      message: errMessage(error),
      code: error instanceof ApiClientError ? error.code : undefined,
    };
  }
}

/** Complete password reset with OTP. Does not auto sign-in. */
export async function platformResetPassword(input: {
  email: string;
  code: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<PlatformAuthStep> {
  const code = digitsOnlyOtp(input.code);
  if (code.length !== 6) {
    return { kind: "error", message: "Enter the 6-digit code from your email." };
  }
  if (input.newPassword.length < 8) {
    return { kind: "error", message: "Password must be at least 8 characters." };
  }
  if (input.newPassword !== input.confirmPassword) {
    return { kind: "error", message: "Passwords do not match." };
  }
  try {
    await api.resetPassword({
      email: input.email,
      code,
      newPassword: input.newPassword,
    });
    return {
      kind: "message",
      message: "Password updated. You can sign in now.",
    };
  } catch (error) {
    return {
      kind: "error",
      message: errMessage(error),
      code: error instanceof ApiClientError ? error.code : undefined,
    };
  }
}
